import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  useMigrateLegacyToSmartTable,
  type LegacySmartTableMigrationSourceType,
} from "@/queries/smart-tables";
import { ToastType } from "@/enums";
import { routePaths } from "@/utils/routePaths";
import { displayErrorToast, displayToast } from "@/utils/toast";

const SOURCE_LABEL_BY_TYPE: Record<LegacySmartTableMigrationSourceType, string> = {
  report: "evaluation/report",
  dataset_group: "dataset",
};

interface UseLegacyEntityTableConversionParams {
  sourceType: LegacySmartTableMigrationSourceType;
  sourceId?: number;
}

export const useLegacyEntityTableConversion = ({
  sourceType,
  sourceId,
}: UseLegacyEntityTableConversionParams) => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const migrateLegacyToSmartTable = useMigrateLegacyToSmartTable();

  const convertToTable = useCallback(() => {
    if (!sourceId) {
      displayErrorToast("Unable to convert: missing legacy entity ID");
      return;
    }

    migrateLegacyToSmartTable.mutate(
      {
        source_type: sourceType,
        source_id: sourceId,
      },
      {
        onSuccess: response => {
          const migratedTableId = response.smart_table?.id;

          if (workspaceId && migratedTableId) {
            navigate(routePaths.workspaceSmartTable(workspaceId, migratedTableId));
          }

          const sourceLabel = SOURCE_LABEL_BY_TYPE[sourceType];
          displayToast(`Converted legacy ${sourceLabel} to Table`, ToastType.success);
        },
        onError: () => {
          displayErrorToast("Failed to convert this legacy view to a Table");
        },
      },
    );
  }, [migrateLegacyToSmartTable, navigate, sourceId, sourceType, workspaceId]);

  return {
    convertToTable,
    isConverting: migrateLegacyToSmartTable.isPending,
  };
};
